/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * IconButton.tsx — Ico\1 \2utton (services/operator-web-app).
 */

import type { ButtonHTMLAttributes } from 'react';
import type { ReactNode } from 'react';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  'aria-label': string;
  icon: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'ghost';
}

const sizeClasses = {
  sm: 'p-1.5',
  md: 'p-2',
  lg: 'p-2.5',
};

const variantClasses = {
  primary: 'text-white bg-sky-600 hover:bg-sky-700 focus:ring-sky-500',
  secondary:
    'focus:ring-sky-500',
  ghost:
    'focus:ring-sky-500',
};

const variantStyles: Record<string, React.CSSProperties> = {
  primary: {},
  secondary: {
    color: 'var(--text-secondary)',
    background: 'var(--toggle-bg)',
  },
  ghost: {
    color: 'var(--text-secondary)',
  },
};

export function IconButton({
  icon,
  size = 'md',
  variant = 'ghost',
  className = '',
  ...rest
}: IconButtonProps) {
  const classes = [
    'inline-flex items-center justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-colors',
    sizeClasses[size],
    variantClasses[variant],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={classes} style={variantStyles[variant]} {...rest}>
      <span className="shrink-0" aria-hidden>
        {icon}
      </span>
    </button>
  );
}
